import { useNavigate, useOutletContext } from 'react-router-dom'

import {
  Box,
  type Colors,
  ColumnSplit,
  Container,
  Title,
} from '@factorialco/deprecated-design-system'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import AnonymousBanner from 'modules/enps/components/Banner/AnonymousBanner'
import { useQuestionGroup } from 'modules/enps/hooks/useQuestionGroup'
import type Form from 'modules/enps/store/models/Form'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import UserBlock from 'modules/surveys/components/UserBlock'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'

import BlankState from './BlankState'
import { DefaultBlankState } from './DefaultBlankState'
import ResponseQuestionsWrapper from './Response/ResponseQuestionsWrapper'
import StopedBlankState from './StopedBlankState'

interface BannerElement {
  color: Colors
  message: string
}

const Answers = () => {
  const { form, group, pathname } = useOutletContext<{
    form: Form
    group: QuestionGroup
    pathname: string
  }>()

  const canEditConfig = form && form.policy.can('editEnpsForm')
  const { loading, questionGroup, refresh } = useQuestionGroup(group?.id ? group.id : null)

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { employee } = useCurrentSession()

  const { description, name, next_cycle, start_on, state } = form.attributes
  const today = moment().startOf('day')
  const diffDays = moment(start_on).diff(today, 'days')
  const scheduled = next_cycle && diffDays > 0 && form.hasSchedule

  const formatDate = (date: string) => moment(date).format('D MMMM YYYY')

  function bannerElements(): BannerElement {
    if (form.isFinished)
      return {
        color: 'malibu200',
        message: i18n.t('enps.form.answer.start_date.finished'),
      }

    if ((form.isDraft && start_on) || scheduled) {
      return {
        color: 'grey400',
        message:
          i18n.t('enps.form.answer.start_date.draft') +
          (start_on ? formatDate(start_on) : '-') +
          '. ' +
          i18n.t('enps.form.answer.anonymous'),
      }
    } else
      return {
        color: 'champagne400',
        message:
          i18n.t('enps.form.answer.start_date.active') +
          (next_cycle ? formatDate(next_cycle) : '-') +
          '. ' +
          i18n.t('enps.form.answer.anonymous'),
      }
  }

  const { color, message } = bannerElements()

  const preConfigEnps = !loading && form.isDraft && !start_on
  const stopedEnps = !loading && form.isFinished && start_on
  const startedEnps = !loading && !form.isFinished && !(form.isDraft && !start_on)
  const noEditPermissions = !startedEnps && !canEditConfig
  useEffect(() => {
    track(trackingEvents.enps.goToEnpsAnswers, {
      coming_from: pathname,
      status_survey: state,
    })
  }, [state, pathname])

  const canEdit = form && form.policy.can('editEnpsForm')

  const onNavigateToSettings = () => {
    track(trackingEvents.enps.goToEnpsSettings, {
      coming_from: pathname,
      is_launched: form.isActive,
    })

    navigate(navigation.engagement.enps.configuration.fullPath())
  }

  return (
    <ResponsivePage.Body
      showHeader={false}
      actions={{
        primary:
          form && canEdit
            ? [
                {
                  onClick: () => onNavigateToSettings(),
                  icon: 'settings',
                  variant: 'secondary',
                  label: i18n.t('common.settings'),
                } as const,
              ]
            : [],
      }}
    >
      <>
        {startedEnps && (
          <Container fullWidth>
            <AnonymousBanner color={color} message={message} />
          </Container>
        )}
        <Container>
          {loading && <Loading />}
          {noEditPermissions && stopedEnps && <DefaultBlankState />}
          {preConfigEnps && <BlankState />}
          {stopedEnps && !noEditPermissions && <StopedBlankState form={form} />}
          {startedEnps && (
            <Box flexDirection="column" width="full">
              <ColumnSplit colored={false} minWidth="none">
                <Title
                  variant="600"
                  title={name}
                  description={description}
                  illustration="love-hands"
                  illustrationColor="radicalRed1000"
                />
                <Box>
                  {questionGroup && (
                    <UserBlock user={null}>
                      <ResponseQuestionsWrapper
                        form={form}
                        questionGroup={questionGroup}
                        onDraftSaved={refresh}
                      />
                    </UserBlock>
                  )}
                </Box>
              </ColumnSplit>
            </Box>
          )}
        </Container>
      </>
    </ResponsivePage.Body>
  )
}

export default Answers
